import { Flex, Table, type TableColumnType, Text, theme } from '@/components';
import { PinpointMarker } from '@/features/comment/components/pinpoint';
import { useDevToolsStorage } from '@/features/dev-tools/_hooks/local-storage';
import { useProject } from '@/features/project/_hooks/project';
import {
  ORDINARY_EXPENSES_ROW_HEADER_KEYS,
  ORDINARY_REVENUE_EXECUTION_AMOUNT_ROW_HEADER_KEYS,
  ORDINARY_REVENUE_FINALIZED_AMOUNT_ROW_HEADER_KEYS,
  ORDINARY_REVENUE_ROW_HEADER_KEYS,
  type SsProjectDetailedBreakdownTableDataType,
  getSsProjectDetailedBreakdownTableSource,
} from '@/features/project/_utils/ss-project-detailed-breakdown';
import { useConsecutiveFiscalYears } from '@/features/project/projects/detail/activities/_hooks/consecutive';
import { useSegmentExpenditureAndExecution } from '@/features/project/projects/detail/income-and-expenditure/_hooks/segment-expenditure-and-execution';
import { useId } from '@/hooks';
import { useLocaledAmount } from '@/providers';
import {
  type CSSProperties,
  type FC,
  useCallback,
  useLayoutEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { SsDetailedBreakdownDropdownButton } from './SsDetailedBreakdownDropdownButton';

const LEVEL1_COLUMN_WIDTH = 80;
const LEVEL2_COLUMN_WIDTH = 70;
const LEVEL3_COLUMN_WIDTH = 110;
const LEVEL4_COLUMN_WIDTH = 150;

const DATA_COLUMN_MIN_WIDTH = 150;

const BORDER_WIDTH = 1;
const CURRENT_YEAR_COUNT = 1;
const NEXT_YEAR_COUNT = 1;

const ROW_HEADER_COLUMN = <></>;

const NON_CONTENT_WIDTH = 250;

const EMPTY_SPAN_PROPS = {
  children: null,
  props: { rowSpan: 0 },
};

export const BudgetAmountsTable: FC = () => {
  const { t } = useTranslation();
  const { id: projectId } = useId();
  const { unit } = useLocaledAmount();
  const { token } = theme.useToken();
  const { devtoolState } = useDevToolsStorage();
  const [tableWidth, setTableWidth] = useState<number | string | undefined>(
    undefined,
  );

  const project = useProject(projectId);
  const segmentExpenditureAndExecution = useSegmentExpenditureAndExecution({
    projectId,
  });

  const projectYear = project.data?.fiscalYear;

  const consecutiveFiscalYears = useConsecutiveFiscalYears({ id: projectId });
  const { dataSources, pastYears } = getSsProjectDetailedBreakdownTableSource({
    data: segmentExpenditureAndExecution.data,
    consecutiveFiscalYears: consecutiveFiscalYears.data,
    projectYear,
    unit,
    isDebugMode: !!devtoolState?.isDebugMode,
  });

  useLayoutEffect(() => {
    const updateSize = (): void => {
      const dataColumnWidth =
        (pastYears.length + CURRENT_YEAR_COUNT * NEXT_YEAR_COUNT) *
        DATA_COLUMN_MIN_WIDTH;

      const captionColumnWidth =
        DATA_COLUMN_MIN_WIDTH +
        LEVEL1_COLUMN_WIDTH +
        LEVEL2_COLUMN_WIDTH +
        LEVEL3_COLUMN_WIDTH +
        LEVEL4_COLUMN_WIDTH;

      const tableWidth = captionColumnWidth + dataColumnWidth + BORDER_WIDTH;
      const wrapperContainerWidth = window.innerWidth - NON_CONTENT_WIDTH;

      setTableWidth(wrapperContainerWidth <= tableWidth ? '100%' : tableWidth);
    };

    window.addEventListener('resize', updateSize);
    updateSize();

    return () => window.removeEventListener('resize', updateSize);
  }, [pastYears.length]);

  const getCellStyle = useCallback(
    (
      record: SsProjectDetailedBreakdownTableDataType,
      type: 'currentYear' | 'nextYear' | 'pastYear',
      year?: number,
    ) => {
      const alignStyle: CSSProperties = {
        textAlign:
          record.rowHeaderKey === 'managementExpenseGrantOfMonetizationCriteria'
            ? 'left'
            : 'right',
      };

      if (record.rowHeaderKey === 'totalExecutedAmount') {
        return {
          style: { background: token.Table?.rowSelectedBg, ...alignStyle },
        };
      }
      if (
        (type === 'currentYear' && record.yearStatus.currentYearDisabled) ||
        (type === 'pastYear' && record.yearStatus.pastYearDisabled) ||
        (type === 'pastYear' && year !== (projectYear ?? 0) - 1) ||
        (type === 'nextYear' && record.yearStatus.nextYearDisabled)
      ) {
        return { style: { background: '#F2F2F2', ...alignStyle } };
      }
      return { style: alignStyle };
    },
    [projectYear, token.Table?.rowSelectedBg],
  );

  return (
    <>
      <Flex justify="space-between">
        <PinpointMarker
          pinpoint={{
            path: { project: projectId },
            field: 'ss.budget-amounts-table-title',
          }}
        >
          <Text variant="h3" text={t('budget-and-execution-title')} />
        </PinpointMarker>
        <SsDetailedBreakdownDropdownButton />
      </Flex>
      <PinpointMarker
        pinpoint={{
          path: { project: projectId },
          field: 'ss.budget-amounts-table',
        }}
      >
        <div style={{ backgroundColor: '#FAFAFA' }}>
          <Table
            bordered
            pagination={false}
            rowKey="rowHeaderKey"
            dataSource={dataSources}
            style={{ width: tableWidth }}
            columns={[
              {
                title: ROW_HEADER_COLUMN,
                dataIndex: 'level1',
                width: LEVEL1_COLUMN_WIDTH,
                rowScope: 'row',
                fixed: 'left',
                render: (value, record) => {
                  const isInitialBudget =
                    record.rowHeaderKey === 'initialBudget';
                  const isFinalizedAmount =
                    record.rowHeaderKey === 'finalizedAmount';
                  if (!isInitialBudget && !isFinalizedAmount) {
                    return EMPTY_SPAN_PROPS;
                  }
                  return {
                    children: <Text weight="regular" text={value} />,
                    props: {
                      rowSpan: isInitialBudget
                        ? ORDINARY_REVENUE_ROW_HEADER_KEYS.length
                        : ORDINARY_EXPENSES_ROW_HEADER_KEYS.length,
                    },
                  };
                },
              },
              {
                title: ROW_HEADER_COLUMN,
                dataIndex: 'level2',
                colSpan: 3,
                rowScope: 'row',
                fixed: 'left',
                width: LEVEL2_COLUMN_WIDTH,
                render: (value, record) => {
                  const isOrdinaryExpensesFinalizedAmountRowHeaderKeys =
                    ORDINARY_REVENUE_FINALIZED_AMOUNT_ROW_HEADER_KEYS.includes(
                      record.rowHeaderKey,
                    );

                  const isOrdinaryExpensesExecutionAmountRowHeaderKeys =
                    ORDINARY_REVENUE_EXECUTION_AMOUNT_ROW_HEADER_KEYS.includes(
                      record.rowHeaderKey,
                    );

                  if (
                    isOrdinaryExpensesFinalizedAmountRowHeaderKeys ||
                    isOrdinaryExpensesExecutionAmountRowHeaderKeys
                  ) {
                    const isInitialBudget =
                      record.rowHeaderKey === 'initialBudget';
                    const isManagementExpenseGrant =
                      record.rowHeaderKey === 'managementExpenseGrant';
                    if (!isInitialBudget && !isManagementExpenseGrant) {
                      return EMPTY_SPAN_PROPS;
                    }
                    return {
                      children: <Text weight="regular" text={value} />,
                      props: {
                        rowSpan: isOrdinaryExpensesFinalizedAmountRowHeaderKeys
                          ? ORDINARY_REVENUE_FINALIZED_AMOUNT_ROW_HEADER_KEYS.length
                          : ORDINARY_REVENUE_EXECUTION_AMOUNT_ROW_HEADER_KEYS.length,
                      },
                    };
                  }
                  return {
                    children: <Text weight="regular" text={value} />,
                    props: { rowSpan: 1, colSpan: 3 },
                  };
                },
              },
              {
                title: ROW_HEADER_COLUMN,
                dataIndex: 'level3',
                colSpan: 0,
                rowScope: 'row',
                fixed: 'left',
                width: LEVEL3_COLUMN_WIDTH,
                render: (value, record) => {
                  const isOrdinaryExpensesExecutionAmountRowHeaderKeys =
                    ORDINARY_REVENUE_EXECUTION_AMOUNT_ROW_HEADER_KEYS.includes(
                      record.rowHeaderKey,
                    );

                  const isInitialBudget =
                    record.rowHeaderKey === 'initialBudget';
                  if (
                    !isInitialBudget &&
                    !isOrdinaryExpensesExecutionAmountRowHeaderKeys
                  ) {
                    return EMPTY_SPAN_PROPS;
                  }
                  return {
                    children: <Text weight="regular" text={value} />,
                    props: {
                      rowSpan: isInitialBudget ? 7 : 1,
                      colSpan: isOrdinaryExpensesExecutionAmountRowHeaderKeys
                        ? 2
                        : 1,
                    },
                  };
                },
              },
              {
                title: ROW_HEADER_COLUMN,
                dataIndex: 'level4',
                rowScope: 'row',
                colSpan: 0,
                fixed: 'left',
                width: LEVEL4_COLUMN_WIDTH,
                render: (value, record) => {
                  const isOrdinaryExpensesFinalizedAmountRowHeaderKeys =
                    ORDINARY_REVENUE_FINALIZED_AMOUNT_ROW_HEADER_KEYS.includes(
                      record.rowHeaderKey,
                    );
                  if (!isOrdinaryExpensesFinalizedAmountRowHeaderKeys) {
                    return EMPTY_SPAN_PROPS;
                  }
                  return {
                    children: <Text weight="regular" text={value} />,
                  };
                },
              },
              ...pastYears.map<
                TableColumnType<SsProjectDetailedBreakdownTableDataType>
              >((year) => ({
                title: (
                  <Flex justify="flex-end">
                    <Text text={year} />
                  </Flex>
                ),
                dataIndex: year,
                width: DATA_COLUMN_MIN_WIDTH,
                onCell: (record) => getCellStyle(record, 'pastYear', year),
                render: (value) => <Text weight="regular" text={value} />,
              })),

              {
                title: (
                  <Flex justify="flex-end">
                    <Text text={projectYear} />
                  </Flex>
                ),
                dataIndex: 'currentYear',
                width: DATA_COLUMN_MIN_WIDTH,
                onCell: (record) => getCellStyle(record, 'currentYear'),
                render: (value) => <Text weight="regular" text={value} />,
              },
              {
                title: (
                  <Flex justify="flex-end">
                    <Text
                      text={t('fiscal-year-estimation', {
                        year: (projectYear ?? 0) + 1,
                      })}
                    />
                  </Flex>
                ),
                dataIndex: 'nextYear',
                width: DATA_COLUMN_MIN_WIDTH,
                onCell: (record) => getCellStyle(record, 'nextYear'),
                render: (value) => <Text weight="regular" text={value} />,
              },
            ]}
          />
        </div>
      </PinpointMarker>
    </>
  );
};
